/**
 * Отчёт сопоставления названий — живёт во вкладке «Интеграции и импорт».
 *
 * UAT founder 07.08: «Это не справочник, а таблица метча того, что из истории
 * поля приходит, и того, что мы с этим сделали в системе». Верно — поэтому
 * таблица осталась таблицей метча, но переехала туда, где метч уместен:
 * в интеграции. Справочник теперь показывает РЕЗУЛЬТАТ
 * (`TenantCatalogListPage`), а этот экран — что пришло и что с этим стало.
 *
 * Исходный докстринг (контекст реализации):
 * «Культуры хозяйства» — экран очереди разбора матчинга культур (Трек 2, шаг 3).
 *
 * Тенант-facing (живёт вкладкой /company/reference). Показывает термины культур
 * хозяйства (`local_catalog_items`, entity_type='crop') с их статусом матча на
 * системный справочник, счётчики-сводку, фильтр по статусу и действия:
 * подтвердить предложение, изменить (ручной подбор), пометить «не культура».
 * Бэкенд — `api/v1/reference/crop_queue.py` (PR #500).
 *
 * DS: только @/components/ui/* примитивы, semantic Tailwind, без hex.
 */
import { useMemo, useState } from 'react';
import {
  Check,
  RefreshCw,
  Search,
  MoveRight,
  AlertCircle,
  Layers,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { ApiError } from '@/lib/api';
import {
  useTenantCrops,
  useRematch,
  useConfirmMatch,
  type CropMatchStatus,
  type CropQueueItem,
  type CropQueueSummary,
  type NotACropKind,
} from '@/lib/reference/tenantCrops';
import {
  NOT_A_CROP_KIND_LABEL,
  notACropLabel,
} from '@/lib/reference/notACropKinds';
import {
  useFieldStateLabel,
  useLandCategoryLabel,
} from '@/lib/reference/fieldCatalogs';
import { typeBadgeLabel } from './catalog-review/reviewLabels';
import { CropMatchDialog } from './CropMatchDialog';
import { NotACropDialog } from './NotACropDialog';
import { CropMixDialog } from './CropMixDialog';

type FilterKey = CropMatchStatus | 'all';

/** Под-фильтр по подтипу «не культуры» (бэкенд принимает `?kind=`). */
const KIND_FILTERS: ReadonlyArray<{ key: NotACropKind | 'all'; label: string }> =
  [
    { key: 'all', label: 'Все' },
    { key: 'field_state', label: NOT_A_CROP_KIND_LABEL.field_state },
    { key: 'land_parcel', label: NOT_A_CROP_KIND_LABEL.land_parcel },
    { key: 'crop_mix', label: NOT_A_CROP_KIND_LABEL.crop_mix },
  ];

function errMsg(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.detail : fallback;
}

/**
 * Чип состава тенант-культуры-смеси: «смесь: Люцерна 60% · Эспарцет 40%».
 * Длинный состав сворачиваем в «смесь из N культур», чтобы строка таблицы не
 * разъезжалась.
 */
function mixLabel(item: CropQueueItem): string | null {
  const parts = item.components ?? [];
  const count = item.components_count ?? parts.length;
  if (count <= 0) return null;
  if (parts.length === 0 || count > 3) return `смесь из ${count} культур`;
  const text = parts
    .map((c) => {
      const name = c.crop_name_ru ?? 'без названия';
      const share = c.share_pct === null ? null : Number(c.share_pct);
      return share === null || Number.isNaN(share)
        ? name
        : `${name} ${Number(share.toFixed(2))}%`;
    })
    .join(' · ');
  return text.length > 60 ? `смесь из ${count} культур` : `смесь: ${text}`;
}

/** «12 полей» / «1 поле» / «нет посевов». */
function fieldCountLabel(n: number): string {
  if (n <= 0) return 'нет посевов';
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word = 'полей';
  if (mod10 === 1 && mod100 !== 11) word = 'поле';
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20))
    word = 'поля';
  return `${n} ${word}`;
}

function confidencePct(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return `${Math.round(v * 100)}%`;
}

// ─── summary cards ───────────────────────────────────────────────────────────

interface CardDef {
  key: FilterKey;
  label: string;
  value: number;
  tone: 'default' | 'good' | 'attn' | 'queue' | 'off';
}

function summaryCards(s: CropQueueSummary): CardDef[] {
  return [
    { key: 'all', label: 'всего названий', value: s.total, tone: 'default' },
    { key: 'matched', label: 'сопоставлено', value: s.matched, tone: 'good' },
    {
      key: 'suggested',
      label: 'нужно подтвердить',
      value: s.suggested,
      tone: 'attn',
    },
    {
      key: 'queued',
      label: 'не разобрано',
      value: s.queued,
      tone: 'queue',
    },
    {
      key: 'not_a_crop',
      label: 'не культуры',
      value: s.not_a_crop,
      tone: 'off',
    },
  ];
}

const TONE_VALUE: Record<CardDef['tone'], string> = {
  default: 'text-foreground',
  good: 'text-primary',
  attn: 'text-accent-brand',
  queue: 'text-foreground',
  off: 'text-muted-foreground',
};

// ─── status badge ────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'matched':
      return <Badge variant="primary">Сопоставлено</Badge>;
    case 'suggested':
      return <Badge variant="accent-brand">Нужно подтвердить</Badge>;
    case 'queued':
      return <Badge variant="outline">Не разобрано</Badge>;
    case 'not_a_crop':
      return <Badge variant="secondary">Не культура</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

/**
 * Класс культуры — унаследован от `reference.crops` через JOIN, не хранится
 * в LCI (см. `crop_class` на `CropQueueItem`, PR9 MVP crops). Показываем
 * только если канон привязан — иначе агроному нечего наследовать.
 * Соглашение цветов: perennial (много лет) — primary, winter — accent-brand,
 * annual — muted (нейтральный, дефолт для «яровой однолетней»).
 */
function CropClassBadge({ cropClass }: { cropClass: CropQueueItem['crop_class'] }) {
  if (!cropClass) return null;
  switch (cropClass) {
    case 'perennial':
      return <Badge variant="primary">Многолетник</Badge>;
    case 'winter':
      return <Badge variant="accent-brand">Озимая</Badge>;
    case 'annual':
      return <Badge variant="outline">Яровая</Badge>;
    default:
      return null;
  }
}

// ─── page ────────────────────────────────────────────────────────────────────

export function TenantCropsPage() {
  // Р-1/Р-4: экран открывается СПИСКОМ для ежедневной работы, а не очередью
  // разбора. Разбор переехал во вкладку «Интеграции и импорт».
  const [filter, setFilter] = useState<FilterKey>('all');
  // Подписи «не культура · <значение>» — из справочников с сервера.
  const fieldStateLabel = useFieldStateLabel();
  const landCategoryLabel = useLandCategoryLabel();
  const [kindFilter, setKindFilter] = useState<NotACropKind | 'all'>('all');
  const [dialogItem, setDialogItem] = useState<CropQueueItem | null>(null);
  const [notACropItem, setNotACropItem] = useState<CropQueueItem | null>(null);
  const [mixItem, setMixItem] = useState<CropQueueItem | null>(null);

  const query = useTenantCrops(
    filter,
    filter === 'not_a_crop' && kindFilter !== 'all' ? kindFilter : null,
  );
  const rematch = useRematch();
  const confirm = useConfirmMatch();

  const summary = query.data?.summary;
  const items = query.data?.items ?? [];

  const cards = useMemo(
    () => (summary ? summaryCards(summary) : []),
    [summary],
  );

  function handleRematch() {
    rematch.mutate(undefined, {
      onSuccess: (r) =>
        toast.success(
          `Разбор завершён: ${r.matched} сопоставлено, ${r.suggested} на подтверждение, ${r.queued} на разбор.`,
        ),
      onError: (e) => toast.error(errMsg(e, 'Не удалось запустить разбор')),
    });
  }

  function handleConfirm(item: CropQueueItem) {
    confirm.mutate(
      { itemId: item.id },
      {
        onSuccess: () =>
          toast.success(`«${item.local_name ?? ''}» подтверждено`),
        onError: (e) => toast.error(errMsg(e, 'Не удалось подтвердить')),
      },
    );
  }

  const rowBusy = confirm.isPending;

  return (
    <div className="space-y-5">
      {/* Заголовок раздела */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
        <h2 className="text-lg font-semibold text-foreground">
          Сопоставление названий
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Что пришло из истории полей и импортов и что система с этим сделала.
          Сами записи справочника — на вкладке «Справочник»; сюда попадает то,
          что ещё не стало записью, и то, как оно сопоставилось.
        </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={rematch.isPending}
          onClick={handleRematch}
        >
          <RefreshCw
            className={cn('mr-2 h-4 w-4', rematch.isPending && 'animate-spin')}
          />
          Подобрать совпадения
        </Button>
      </div>

      {/* Счётчики */}
      {query.isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {cards.map((c) => {
            const active = filter === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setFilter(c.key)}
                className={cn(
                  'rounded-lg border bg-card p-3 text-left transition-colors',
                  active
                    ? 'border-primary ring-1 ring-primary'
                    : 'border-border hover:border-primary/40',
                )}
              >
                <div className={cn('text-2xl font-bold', TONE_VALUE[c.tone])}>
                  {c.value}
                </div>
                <div className="text-xs text-muted-foreground">{c.label}</div>
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Второго баннера про разбор здесь НЕТ намеренно (AK-546 ч.2).
          Одна работа — одна дверь и одно число: призыв к разбору живёт в
          `CatalogReviewBanner` над этим экраном и считает по порциям. Старая
          плашка «N нужно подтвердить» показывала ПЕРЕСЕКАЮЩУЮСЯ цифру
          (`summary.suggested` включает строки, уже лежащие в порциях) и вела во
          вторую дверь — на экране, который мы объявили спокойным списком для
          ежедневной работы, это било по простоте освоения сильнее, чем
          помогало. Ручной прогон матчера остался кнопкой в шапке. */}

      {/* Фильтр-чипы */}
      {summary && (
        <div className="flex flex-wrap gap-2">
          {(
            [
              { key: 'suggested', label: 'Нужно внимание', n: summary.suggested },
              { key: 'all', label: 'Все', n: summary.total },
              { key: 'matched', label: 'Сопоставлено', n: summary.matched },
              { key: 'queued', label: 'Не разобрано', n: summary.queued },
              { key: 'not_a_crop', label: 'Не культуры', n: summary.not_a_crop },
            ] as { key: FilterKey; label: string; n: number }[]
          ).map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setFilter(c.key)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm transition-colors',
                filter === c.key
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-foreground hover:bg-accent',
              )}
            >
              {c.label} · {c.n}
            </button>
          ))}
        </div>
      )}

      {/* Под-фильтр по подтипу «не культуры» */}
      {filter === 'not_a_crop' && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Что это:</span>
          {KIND_FILTERS.map((k) => (
            <button
              key={k.key}
              type="button"
              onClick={() => setKindFilter(k.key)}
              aria-pressed={kindFilter === k.key}
              className={cn(
                'rounded-full border px-3 py-1 text-xs transition-colors',
                kindFilter === k.key
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-card text-muted-foreground hover:bg-accent',
              )}
            >
              {k.label}
            </button>
          ))}
        </div>
      )}

      {/* Список */}
      {query.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      ) : query.isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Не удалось загрузить культуры</AlertTitle>
          <AlertDescription className="flex items-center gap-3">
            Проверьте соединение и попробуйте снова.
            <Button
              variant="outline"
              size="sm"
              onClick={() => query.refetch()}
            >
              Повторить
            </Button>
          </AlertDescription>
        </Alert>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm font-medium text-foreground">
            {filter === 'all'
              ? 'Пока нет названий культур'
              : 'В этом статусе ничего нет'}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Названия культур появляются из ваших посевов и из того, что вы
            разбираете во вкладке «Интеграции и импорт».
          </p>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[24%]">Ваше название</TableHead>
                <TableHead className="w-[10%]">Тип</TableHead>
                <TableHead className="w-[10%]">Посевов</TableHead>
                <TableHead className="w-[24%]">Предложение системы</TableHead>
                <TableHead className="w-[12%]">Статус</TableHead>
                <TableHead className="w-[26%] text-right">Действие</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium text-foreground">
                    <div className="flex flex-col gap-1">
                      <span>{item.local_name ?? '—'}</span>
                      {mixLabel(item) && (
                        <Badge
                          variant="secondary"
                          className="w-fit max-w-full whitespace-normal text-left font-normal"
                        >
                          {mixLabel(item)}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {/* Решение А: пар, АМП и угодья лежат в ОБЩЕМ списке
                        культур — отличает их только бейдж типа. Внутри системы
                        это по-прежнему раздельные сущности (Р-5). */}
                    <Badge variant="outline">
                      {typeBadgeLabel(
                        item.not_a_crop_kind ?? null,
                        (item.components_count ?? 0) > 0,
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {fieldCountLabel(item.field_count)}
                  </TableCell>
                  <TableCell>
                    {item.match_status === 'not_a_crop' ? (
                      <Badge variant="secondary">
                        {notACropLabel(item, {
                          fieldState: fieldStateLabel,
                          landCategory: landCategoryLabel,
                        })}
                      </Badge>
                    ) : item.canonical_name ? (
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="inline-flex items-center gap-1 text-foreground">
                          <MoveRight className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-medium">
                            {item.canonical_name}
                          </span>
                          {item.match_status === 'suggested' && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              {confidencePct(item.match_confidence)}
                            </span>
                          )}
                        </span>
                        {item.growing_goal_name_ru && (
                          <Badge variant="primary">
                            {item.growing_goal_name_ru}
                          </Badge>
                        )}
                        <CropClassBadge cropClass={item.crop_class} />
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        нет уверенного совпадения
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={item.match_status} />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap justify-end gap-2">
                      {item.match_status === 'suggested' && item.ref_id && (
                        <Button
                          size="sm"
                          disabled={rowBusy}
                          onClick={() => handleConfirm(item)}
                        >
                          <Check className="mr-1 h-3.5 w-3.5" />
                          Подтвердить
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={rowBusy}
                        onClick={() => setDialogItem(item)}
                      >
                        {item.match_status === 'matched' ? (
                          'Изменить'
                        ) : (
                          <>
                            <Search className="mr-1 h-3.5 w-3.5" />
                            Найти вручную
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={rowBusy}
                        onClick={() => setMixItem(item)}
                      >
                        <Layers className="mr-1 h-3.5 w-3.5" />
                        {(item.components_count ?? 0) > 0
                          ? 'Состав смеси'
                          : 'Сделать смесью'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={rowBusy}
                        onClick={() => setNotACropItem(item)}
                      >
                        {item.match_status === 'not_a_crop'
                          ? 'Уточнить'
                          : 'Не культура'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CropMatchDialog
        item={dialogItem}
        open={dialogItem !== null}
        onOpenChange={(o) => !o && setDialogItem(null)}
        onRequestNotACrop={(it) => {
          setDialogItem(null);
          setNotACropItem(it);
        }}
      />

      <NotACropDialog
        item={notACropItem}
        open={notACropItem !== null}
        onOpenChange={(o) => !o && setNotACropItem(null)}
      />

      <CropMixDialog
        item={mixItem}
        open={mixItem !== null}
        onOpenChange={(o) => !o && setMixItem(null)}
      />
    </div>
  );
}

export default TenantCropsPage;
